import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { LandingPromoBanner } from '@sorare/core/src/components/Season2026/LandingPromoBanner/index.tsx';
import { Ambassadors } from '@sorare/core/src/components/landing/Ambassadors';
import { CommunityBlock } from '@sorare/core/src/components/landing/CommunityBlock';
import { Compete } from '@sorare/core/src/components/landing/Compete';
import { HeroVideo } from '@sorare/core/src/components/landing/HeroVideo';
import type { League } from '@sorare/core/src/components/landing/Leagues';
import { Leagues } from '@sorare/core/src/components/landing/Leagues';
import { Scout } from '@sorare/core/src/components/landing/Scout';
import { Win } from '@sorare/core/src/components/landing/Win';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import { LeagueImage } from '@sorare/gameplay/src/components/league/LeagueImage';

import mobileCardsAvif from '#src/components/landing/assets/compete/cards-mobile.avif';
import mobileCards from '#src/components/landing/assets/compete/cards-mobile.webp';
import competeCardsAvif from '#src/components/landing/assets/compete/cards.avif';
import competeCards from '#src/components/landing/assets/compete/cards.webp';
import fieldAvif from '#src/components/landing/assets/compete/field.avif';
import field from '#src/components/landing/assets/compete/field.webp';
import scoutBackgroundAvif from '#src/components/landing/assets/scout/background.avif';
import scoutBackground from '#src/components/landing/assets/scout/background.webp';
import commonAvif from '#src/components/landing/assets/scout/leagues/common.avif';
import common from '#src/components/landing/assets/scout/leagues/common.webp';
import limitedAvif from '#src/components/landing/assets/scout/leagues/limited.avif';
import limited from '#src/components/landing/assets/scout/leagues/limited.webp';
import rareAvif from '#src/components/landing/assets/scout/leagues/rare.avif';
import rare from '#src/components/landing/assets/scout/leagues/rare.webp';
import superRareAvif from '#src/components/landing/assets/scout/leagues/super-rare.avif';
import superRare from '#src/components/landing/assets/scout/leagues/super-rare.webp';
import uniqueAvif from '#src/components/landing/assets/scout/leagues/unique.avif';
import unique from '#src/components/landing/assets/scout/leagues/unique.webp';
import scoutPlayersAvif from '#src/components/landing/assets/scout/players.avif';
import scoutPlayers from '#src/components/landing/assets/scout/players.webp';
import winBackgroundAvif from '#src/components/landing/assets/win/background.avif';
import winBackground from '#src/components/landing/assets/win/background.webp';
import winCardsAvif from '#src/components/landing/assets/win/card.avif';
import winCards from '#src/components/landing/assets/win/card.webp';
import cashAvif from '#src/components/landing/assets/win/cash.avif';
import cash from '#src/components/landing/assets/win/cash.webp';
import ticketsAvif from '#src/components/landing/assets/win/tickets.avif';
import tickets from '#src/components/landing/assets/win/tickets.webp';
import vipAvif from '#src/components/landing/assets/win/vip.avif';
import vip from '#src/components/landing/assets/win/vip.webp';

import { FAQ } from './FAQ/index.tsx';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  gap: var(--quadruple-unit);
  overflow: hidden;
  position: relative;
  padding-bottom: var(--double-unit);
  &.showFAQ {
    padding-bottom: calc(8 * var(--unit));
  }
  @media ${tabletAndAbove} {
    gap: calc(8 * var(--unit));
  }
`;
const Hero = styled.div`
  &.showOtherLeagues {
    @media ${tabletAndAbove} {
      margin-bottom: calc(var(--unit) * 12);
    }
  }
`;
const Players = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  background-image: var(--players);
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center top -5%;
  min-width: 100%;
  max-width: 100%;
  aspect-ratio: 746/481;
  @media ${laptopAndAbove} {
    height: 480px;
    background-position: right center;
  }
`;

const ImgWrapper = styled.div`
  padding: 0 var(--unit);
`;

const assets = {
  scout: {
    players: {
      avif: scoutPlayersAvif,
      pictureUrl: scoutPlayers,
    },
    background: {
      avif: scoutBackgroundAvif,
      pictureUrl: scoutBackground,
    },
  },
  compete: {
    field: {
      avif: fieldAvif,
      pictureUrl: field,
    },
  },
  win: {
    cards: {
      avif: winCardsAvif,
      pictureUrl: winCards,
    },
  },
};

const leagueAssets = [
  {
    id: 'common',
    pictureUrl: common,
    avif: commonAvif,
  },
  {
    id: 'limited',
    pictureUrl: limited,
    avif: limitedAvif,
  },
  {
    id: 'rare',
    pictureUrl: rare,
    avif: rareAvif,
  },
  {
    id: 'super rare',
    pictureUrl: superRare,
    avif: superRareAvif,
  },
  {
    id: 'unique',
    pictureUrl: unique,
    avif: uniqueAvif,
  },
];

type Props = {
  showFAQ?: boolean;
  leagues?: League[];
  leaderboards: So5LeaderboardType[];
};

export const EarlyAccess = ({ showFAQ, leagues, leaderboards }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  return (
    <Root className={classNames({ extraBottomPadding: !showFAQ })}>
      <Hero className={classNames({ showOtherLeagues: !!leagues })}>
        <Container>
          <LandingPromoBanner league="mlb" />
        </Container>
        <HeroVideo
          title={
            <FormattedMessage defaultMessage="A New Era of Fantasy Baseball" />
          }
          subtitle={
            <FormattedMessage defaultMessage="Think you know ball? Create your fantasy club now and compete for VIP tickets, signed jerseys and more." />
          }
        />
        {!!leagues && <Leagues leagues={leagues} />}
      </Hero>
      <Scout
        assets={{
          players: assets.scout.players,
          background: assets.scout.background,
        }}
        title={<FormattedMessage defaultMessage="Build" />}
        description={
          <FormattedMessage defaultMessage="Create your ultimate collection of official MLB digital player cards. Trade, buy, and sell them on the marketplace to keep your edge." />
        }
        ctaLabel={<FormattedMessage {...glossary.joinForFree} />}
        players={
          <Players
            style={
              {
                '--players': `image-set(url(${assets.scout.players.avif}) type('image/avif'), url(${assets.scout.players.pictureUrl}) type('image/webp'))`,
              } as CSSProperties
            }
          />
        }
      >
        {leagueAssets.map(({ id, ...assetProps }) => (
          <LeagueImage key={id} {...assetProps} headerUrl={null} />
        ))}
      </Scout>
      <Compete
        assets={{
          field: {
            avif: fieldAvif,
            pictureUrl: field,
          },
        }}
        title={<FormattedMessage defaultMessage="Compete" />}
        description={
          <FormattedMessage defaultMessage="Set 7 player lineups and compete in daily & bi-weekly fantasy baseball competitions." />
        }
        img={
          <ImgWrapper>
            <picture>
              <source
                srcSet={isTabletAndAbove ? competeCardsAvif : mobileCardsAvif}
                type="image/avif"
              />
              <Img
                src={isTabletAndAbove ? competeCards : mobileCards}
                fetchPriority="low"
                width="1163"
                height="302"
              />
            </picture>
          </ImgWrapper>
        }
      />
      <Container>
        <Win
          assets={{
            ...assets.win,
            background: {
              avif: winBackgroundAvif,
              pictureUrl: winBackground,
            },
          }}
          title={<FormattedMessage defaultMessage="Become a Legend" />}
          description={
            <FormattedMessage defaultMessage="Cement your legacy as the best Manager in the Sorare community. Show off your baseball IQ and build the ultimate collection." />
          }
          ctaLabel={<FormattedMessage {...glossary.joinForFree} />}
          rewards={[
            {
              id: 'vip',
              avif: vipAvif,
              pictureUrl: vip,
              width: 120,
              height: 224,
              label: <FormattedMessage defaultMessage="Vip Experiences" />,
            },
            {
              id: 'card',
              avif: winCardsAvif,
              pictureUrl: winCards,
              width: 104,
              height: 192,
              label: <FormattedMessage defaultMessage="Playable Cards" />,
            },
            {
              id: 'cash',
              avif: cashAvif,
              pictureUrl: cash,
              width: 224,
              height: 248,
              label: <FormattedMessage {...glossary.cashRewards} />,
            },
            {
              id: 'tickets',
              avif: ticketsAvif,
              pictureUrl: tickets,
              width: 200,
              height: 128,
              label: <FormattedMessage defaultMessage="Game Tickets" />,
            },
          ]}
        />
      </Container>
      <CommunityBlock
        leaderboards={leaderboards.map(leaderboardType => ({
          sport: Sport.BASEBALL,
          leaderboardType,
        }))}
      />
      <Container>
        <Ambassadors />
      </Container>
      {!!showFAQ && <FAQ />}
    </Root>
  );
};
